import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, FolderKanban, Contact, ArrowRight, Command } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  'Lead/do pomiaru': 'bg-blue-500/20 text-blue-400',
  'Akceptacja': 'bg-yellow-500/20 text-yellow-400',
  'Brak akceptacji': 'bg-red-500/20 text-red-400',
  'Opłacone': 'bg-emerald-500/20 text-emerald-400',
  'Dostawa': 'bg-purple-500/20 text-purple-400',
  'W realizacji': 'bg-orange-500/20 text-orange-400',
  'Zakończono': 'bg-green-500/20 text-green-400',
};

export const GlobalSearch = ({ open: externalOpen, onClose: externalClose }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen || internalOpen;
  const setOpen = (val) => {
    setInternalOpen(val);
    if (!val && externalClose) externalClose();
  };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const searchTimeout = useRef(null);
  const navigate = useNavigate();

  // Sync external open state
  useEffect(() => {
    if (externalOpen) setInternalOpen(true);
  }, [externalOpen]);

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/search`, { params: { q } });
      setResults(res.data);
      setActiveIdx(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => doSearch(val), 200);
  };

  const goTo = (url) => {
    setOpen(false);
    navigate(url);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[activeIdx]) { goTo(results[activeIdx].url); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="global-search-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Modal */}
      <div className="relative mx-auto mt-[15vh] w-full max-w-xl px-4">
        <div className="bg-[var(--crm-bg-main)] border border-[var(--crm-border)] rounded-xl shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-[var(--crm-border)]">
            <Search className="w-5 h-5 text-[var(--crm-text-secondary)] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Szukaj projektów, klientów..."
              data-testid="global-search-input"
              className="flex-1 bg-transparent text-[var(--crm-text-primary)] placeholder-slate-500 py-4 text-base outline-none"
              autoComplete="off"
            />
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-mono text-[var(--crm-text-muted)] bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto">
            {loading && query.length >= 2 && (
              <div className="px-4 py-6 text-center text-[var(--crm-text-muted)] text-sm">Szukam...</div>
            )}

            {!loading && query.length >= 2 && results.length === 0 && (
              <div className="px-4 py-8 text-center text-[var(--crm-text-muted)] text-sm" data-testid="global-search-no-results">
                Brak wyników dla "{query}"
              </div>
            )}

            {results.length > 0 && (
              <div className="py-2" data-testid="global-search-results">
                {results.map((r, idx) => (
                  <button
                    key={`${r.type}-${r.id}`}
                    onClick={() => goTo(r.url)}
                    data-testid={`search-result-${r.type}-${r.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      idx === activeIdx ? 'bg-[#FF6B35]/10' : 'hover:bg-[var(--crm-bg-card-hover)]'
                    }`}
                    onMouseEnter={() => setActiveIdx(idx)}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      r.type === 'project' ? 'bg-blue-500/15' : 'bg-emerald-500/15'
                    }`}>
                      {r.type === 'project'
                        ? <FolderKanban className="w-4.5 h-4.5 text-blue-400" />
                        : <Contact className="w-4.5 h-4.5 text-emerald-400" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[var(--crm-text-primary)] text-sm font-medium truncate">{r.name}</span>
                        {r.status && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[r.status] || 'bg-slate-500/20 text-[var(--crm-text-secondary)]'}`}>
                            {r.status}
                          </span>
                        )}
                      </div>
                      {r.subtitle && <p className="text-[var(--crm-text-muted)] text-xs truncate">{r.subtitle}</p>}
                    </div>
                    <ArrowRight className={`w-4 h-4 shrink-0 transition-opacity ${idx === activeIdx ? 'text-[#FF6B35] opacity-100' : 'opacity-0'}`} />
                  </button>
                ))}
              </div>
            )}

            {query.length < 2 && (
              <div className="px-4 py-8 text-center text-[var(--crm-text-muted)] text-sm">
                Wpisz min. 2 znaki aby wyszukać
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-[var(--crm-border)] text-[10px] text-slate-600">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded text-[var(--crm-text-muted)]">↑↓</kbd> nawigacja</span>
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded text-[var(--crm-text-muted)]">↵</kbd> otwórz</span>
            </div>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded text-[var(--crm-text-muted)]">esc</kbd> zamknij</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SearchTrigger = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="global-search-trigger"
    data-onboarding="search-trigger"
    className="flex items-center gap-2 px-3 py-1.5 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] rounded-lg transition-colors hover:border-[#4a6a9e] group"
    title="Szukaj (Ctrl+K)"
  >
    <Search className="w-4 h-4" />
    <span className="text-xs hidden lg:inline">Szukaj...</span>
    <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 bg-[#0d1829] border border-[var(--crm-border)] rounded group-hover:text-[var(--crm-text-secondary)]">
      <Command className="w-2.5 h-2.5" />K
    </kbd>
  </button>
);
